<template>
  <div class="slider">
    <var-style-provider :style-vars="styleVars">
      <var-slider
        v-model="inputValue"
        isScroll="true"
        :label-visible="labelVisible"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        @change="onInput"
        :data-testid="inputTestId"
      />
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import './Slider.scss';
import type { LDTheme } from 'ewi-ui-helpers/types';

export interface SliderProps {
  theme: LDTheme;
  modelValue: number | number[];
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  inputTestId?: string;
  labelVisible?: 'always' | 'never' | 'normal';
}

defineOptions({
  name: 'Slider',
});

const props = withDefaults(defineProps<SliderProps>(), {
  theme: 'light',
  inputTestId: 'slider-input',
  labelVisible: 'never',
  disabled: false,
});

const emit = defineEmits(['update:modelValue']);

const lightTheme = {
  '--slider-track-fill-background': 'var(--SysLightPrimary)',
  '--slider-track-background': 'var(--SysLightSurfaceVariant)',
  '--slider-thumb-block-background': 'var(--SysLightPrimary)',
  '--slider-thumb-label-background': 'var(--SysLightPrimary)',
  '--slider-thumb-label-text-color': 'var(--SysLightOnPrimary)',
  '--slider-disabled-opacity': '12%',
};

const darkTheme = {
  '--slider-track-fill-background': 'var(--SysDarkPrimary)',
  '--slider-track-background': 'var(--SysDarkSurfaceVariant)',
  '--slider-thumb-block-background': 'var(--SysDarkPrimary)',
  '--slider-thumb-label-background': 'var(--SysDarkPrimary)',
  '--slider-thumb-label-text-color': 'var(--SysLightOnPrimary)',
  '--slider-disabled-opacity': '25%',
};

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);
const inputValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newValue) => {
    inputValue.value = newValue;
  },
);

const onInput = (value: number | number[]) => {
  emit('update:modelValue', value);
};
</script>
